import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Skeleton,
  Avatar, Divider, IconButton, Tooltip, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Button, TableContainer, Table, TableHead,
  TableBody, TableRow, TableCell,
} from '@mui/material';
import {
  People, DirectionsCar, AssignmentTurnedIn, VerifiedUser,
  ReportProblem, ConfirmationNumber, BatteryChargingFull,
  BadgeOutlined, SwapHoriz, Refresh, TrendingUp,
  ElectricScooter, Map, Build, AttachMoney, Payment,
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer, Legend, PieChart,
  Pie, Cell,
} from 'recharts';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, color, loading }) => (
  <Card sx={{
    borderRadius: 1,
    bgcolor: `${color}11`,
    border: `1px solid ${color}`,
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    overflow: 'hidden',
    transition: 'transform 0.15s, box-shadow 0.15s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 24px rgba(0,0,0,0.12)' },
  }}>
    <CardContent sx={{ p: 2 }}>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="rounded" width={44} height={44} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="55%" height={28} />
            <Skeleton width="80%" height={14} />
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Icon bubble */}
          <Box sx={{
            width: 44, height: 44, borderRadius: 1,
            background: `linear-gradient(135deg, ${color}22, ${color}22)`,
            border: `1px solid ${color}30`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon sx={{ color, fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: '#0f172a', lineHeight: 1.15 }}>
              {(value ?? 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap>
              {label}
            </Typography>
          </Box>
        </Box>
      )}
    </CardContent>
  </Card>
  // <Card sx={{
  //   borderRadius: 1,
  //   boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
  //   overflow: 'hidden',
  //   transition: 'transform 0.15s, box-shadow 0.15s',
  //   '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 24px rgba(0,0,0,0.12)' },
  // }}>
  //   {/* Top colour strip */}
  //   <Box sx={{ height: 4, background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
  //   <CardContent sx={{ p: 2 }}>
  //     {loading ? (
  //       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
  //         <Skeleton variant="rounded" width={44} height={44} />
  //         <Box sx={{ flex: 1 }}>
  //           <Skeleton width="55%" height={28} />
  //           <Skeleton width="80%" height={14} />
  //         </Box>
  //       </Box>
  //     ) : (
  //       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
  //         {/* Icon bubble */}
  //         <Box sx={{
  //           width: 44, height: 44, borderRadius: 2,
  //           background: `linear-gradient(135deg, ${color}22, ${color}11)`,
  //           border: `1px solid ${color}30`,
  //           display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  //         }}>
  //           <Icon sx={{ color, fontSize: 22 }} />
  //         </Box>
  //         <Box sx={{ flex: 1, minWidth: 0 }}>
  //           <Typography variant="h5" fontWeight={800} sx={{ color: '#0f172a', lineHeight: 1.15 }}>
  //             {(value ?? 0).toLocaleString()}
  //           </Typography>
  //           <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap>
  //             {label}
  //           </Typography>
  //         </Box>
  //       </Box>
  //     )}
  //   </CardContent>
  // </Card>
);

// ── Section header ────────────────────────────────────────────────────────────
const SectionHeader = ({ title, icon: Icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    <Icon sx={{ fontSize: 18, color: '#1a237e' }} />
    <Typography variant="subtitle1" fontWeight={700} color="#1a237e">{title}</Typography>
  </Box>
);

const KYC_COLOR = { pending: 'warning', approved: 'success', rejected: 'error', review: 'info' };
const PIE_COLORS = ['#16a34a', '#1565c0', '#d97706', '#dc2626'];
const STATUS_COLOR = { active: 'success', assigned: 'primary', maintenance: 'warning', retired: 'error' };

const battColor = (pct) => {
  const v = parseInt(pct ?? 0);
  if (v >= 60) return '#16a34a';
  if (v >= 30) return '#d97706';
  return '#dc2626';
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

// ── Admin Payment Section Component ───────────────────────────────────────────
function AdminPaymentSection() {
  const [paymentStats, setPaymentStats] = useState(null);
  const [vendorBreakdown, setVendorBreakdown] = useState([]);
  const [userBreakdown, setUserBreakdown]     = useState([]);
  const [vehicleBreakdown, setVehicleBreakdown] = useState([]);
  const [vendorPayments, setVendorPayments]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('vendor');

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [statsRes, vendorRes, userRes, vehicleRes, vendorPayRes] = await Promise.all([
        InvokeApi(Config.base_url + apiList.getPaymentStats, {}),
        InvokeApi(Config.base_url + apiList.getPaymentBreakdownVendor, {}),
        InvokeApi(Config.base_url + apiList.getPaymentBreakdownUser, {}),
        InvokeApi(Config.base_url + apiList.getPaymentBreakdownVehicle, {}),
        InvokeApi(Config.base_url + apiList.getVendorPaymentBreakdown, {}),
      ]);
      if (statsRes?.code === '200') setPaymentStats({
        totalCollected:  statsRes.total_collected || 0,  // rent/fine/other only
        totalAdvance:    statsRes.total_advance   || 0,  // shown separately (refundable)
        totalPending:    statsRes.total_pending   || 0,
        totalOverdue:    statsRes.total_overdue   || 0,
        totalPayments:   statsRes.total_payments  || 0,
        activePlans:     statsRes.active_plans    || 0,
        thisMonth:       statsRes.this_month      || 0,
        lastMonth:       statsRes.last_month      || 0,
        byType:          statsRes.by_type         || [],
      });
      if (vendorRes?.code    === '200') setVendorBreakdown(vendorRes.vendors     || []);
      if (userRes?.code      === '200') setUserBreakdown(userRes.users           || []);
      if (vehicleRes?.code   === '200') setVehicleBreakdown(vehicleRes.vehicles  || []);
      if (vendorPayRes?.code === '200') setVendorPayments(vendorPayRes.vendors   || []);
      setLoading(false);
    })();
  }, []);

  if (loading) return (
    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', p: 5 }}>
      <CircularProgress sx={{ color: '#1a237e' }} />
    </Box>
  );

  const ps = paymentStats || {};
  const monthGrowth = ps.lastMonth > 0
    ? (((ps.thisMonth - ps.lastMonth) / ps.lastMonth) * 100).toFixed(1)
    : 0;
  const totalRevenue = (ps.totalCollected || 0) + (ps.totalPending || 0) + (ps.totalOverdue || 0);

  // ── reusable amount badge ──────────────────────────────────────────────────
  const AmtBadge = ({ val, color, bg }) => (
    <Box sx={{ display:'inline-flex', alignItems:'center', bgcolor: bg,
      px: 1.5, py: 0.4, borderRadius: 1, minWidth: 90, justifyContent:'flex-end' }}>
      <Typography fontSize={12} fontWeight={700} sx={{ color }}>
        ₹{parseFloat(val||0).toLocaleString('en-IN')}
      </Typography>
    </Box>
  );

  // ── empty state ────────────────────────────────────────────────────────────
  const Empty = ({ icon: Icon, msg }) => (
    <Box sx={{ py: 7, textAlign: 'center' }}>
      <Icon sx={{ fontSize: 52, color: '#e2e8f0', mb: 1 }} />
      <Typography variant="body2" color="text.secondary">{msg}</Typography>
    </Box>
  );

  return (
    <Box sx={{ mt: 3 }}>

      {/* ── Section header ──────────────────────────────────────────────────── */}
      <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 4, height: 28, borderRadius: 2,
          background: 'linear-gradient(180deg,#1a237e,#1565c0)' }} />
        <Box>
          <Typography variant="h6" fontWeight={800} color="#1a237e">
            Payment Overview
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Real-time collection analytics
          </Typography>
        </Box>
      </Box>

      {/* ── 5 stat cards: revenue + advance + status ───────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label:'Revenue Collected', val: `₹${(ps.totalCollected||0).toLocaleString('en-IN')}`,
            icon: TrendingUp,          color:'#2e7d32', bg:'#f0fdf4', border:'#bbf7d0',
            sub: 'Rent / Fine / Other' },
          { label:'Advance Held',      val: `₹${(ps.totalAdvance||0).toLocaleString('en-IN')}`,
            icon: SwapHoriz,           color:'#0369a1', bg:'#f0f9ff', border:'#bae6fd',
            sub: 'Refundable' },
          { label:'Pending',           val: `₹${(ps.totalPending||0).toLocaleString('en-IN')}`,
            icon: SwapHoriz,           color:'#c2410c', bg:'#fff7ed', border:'#fed7aa',
            sub: 'Due / Partial' },
          { label:'Overdue',           val: `₹${(ps.totalOverdue||0).toLocaleString('en-IN')}`,
            icon: ReportProblem,       color:'#dc2626', bg:'#fef2f2', border:'#fecaca',
            sub: 'Revenue only' },
          { label:'Active Plans',      val: ps.activePlans || 0,
            icon: AssignmentTurnedIn,  color:'#1565c0', bg:'#eff6ff', border:'#bfdbfe',
            sub: 'Last 30 days' },
        ].map(({ label, val, icon: Icon, color, bg, border, sub }) => (
          <Grid item xs={6} sm={4} md={2.4} key={label}>
            <Card sx={{ borderRadius:1, boxShadow:'0 2px 8px rgba(0,0,0,0.06)',
              border:`1px solid ${border}`, bgcolor: bg, p: 0 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display:'flex', alignItems:'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{ width:30, height:30, borderRadius:1,
                    bgcolor:'#fff', border:`1px solid ${border}`,
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Icon sx={{ fontSize:16, color }} />
                  </Box>
                  <Typography fontWeight={800} fontSize={14} sx={{ color, lineHeight:1.1 }} noWrap>
                    {val}
                  </Typography>
                </Box>
                <Typography fontSize={10} fontWeight={600} color="text.secondary" noWrap>{label}</Typography>
                <Typography fontSize={9} color="text.disabled" sx={{ mt: 0.2 }}>{sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Monthly strip — simple ───────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius:1, boxShadow:'0 2px 8px rgba(0,0,0,0.06)',
            border:'1px solid #e2e8f0', p:0 }}>
            <CardContent sx={{ p:2, '&:last-child':{ pb:2 } }}>
              <Typography fontSize={10} fontWeight={700} color="text.secondary"
                sx={{ textTransform:'uppercase', letterSpacing:.6, mb:.5 }}>This Month</Typography>
              <Typography fontWeight={800} fontSize={18} color="#15803d">
                ₹{(ps.thisMonth||0).toLocaleString('en-IN')}
              </Typography>
              <Chip size="small"
                label={`${Number(monthGrowth)>=0?'+':''}${monthGrowth}% vs last month`}
                sx={{ mt:.5, height:18, fontSize:10, fontWeight:700,
                  bgcolor: Number(monthGrowth)>=0?'#dcfce7':'#fee2e2',
                  color:   Number(monthGrowth)>=0?'#15803d':'#dc2626' }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius:1, boxShadow:'0 2px 8px rgba(0,0,0,0.06)',
            border:'1px solid #e2e8f0', p:0 }}>
            <CardContent sx={{ p:2, '&:last-child':{ pb:2 } }}>
              <Typography fontSize={10} fontWeight={700} color="text.secondary"
                sx={{ textTransform:'uppercase', letterSpacing:.6, mb:.5 }}>Last Month</Typography>
              <Typography fontWeight={800} fontSize={18} color="#1e40af">
                ₹{(ps.lastMonth||0).toLocaleString('en-IN')}
              </Typography>
              <Typography fontSize={11} color="text.secondary" sx={{ mt:.5 }}>
                Revenue only
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius:1, boxShadow:'0 2px 8px rgba(0,0,0,0.06)',
            border:'1px solid #e2e8f0', p:0 }}>
            <CardContent sx={{ p:2, '&:last-child':{ pb:2 } }}>
              <Typography fontSize={10} fontWeight={700} color="text.secondary"
                sx={{ textTransform:'uppercase', letterSpacing:.6, mb:.5 }}>Total Revenue</Typography>
              <Typography fontWeight={800} fontSize={18} color="#1a237e">
                ₹{totalRevenue.toLocaleString('en-IN')}
              </Typography>
              <Typography fontSize={11} color="text.secondary" sx={{ mt:.5 }}>
                {ps.totalPayments||0} total payments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Transaction type breakdown strip ────────────────────────────────── */}
      {ps.byType?.length > 0 && (
        <Card sx={{ borderRadius:1, boxShadow:'0 2px 8px rgba(0,0,0,0.06)', border:'1px solid #e2e8f0', mb: 2 }}>
          <CardContent sx={{ p:2, '&:last-child':{ pb:2 } }}>
            <Typography fontSize={10} fontWeight={700} color="text.secondary"
              sx={{ textTransform:'uppercase', letterSpacing:.6, mb:1.5 }}>By Transaction Type</Typography>
            <Box sx={{ display:'flex', gap: 2, flexWrap:'wrap' }}>
              {ps.byType.map(t => {
                const META = {
                  rent:    { label:'Rent',    color:'#1d4ed8', bg:'#eff6ff' },
                  advance: { label:'Advance', color:'#0369a1', bg:'#f0f9ff', note:'Refundable' },
                  fine:    { label:'Fine',    color:'#dc2626', bg:'#fef2f2' },
                  other:   { label:'Other',   color:'#475569', bg:'#f8fafc' },
                };
                const m = META[t.transaction_type] || { label: t.transaction_type, color:'#475569', bg:'#f8fafc' };
                return (
                  <Box key={t.transaction_type} sx={{ flex:'1 1 140px', p:1.5, borderRadius:1.5, bgcolor: m.bg, border:`1px solid ${m.color}22` }}>
                    <Box sx={{ display:'flex', alignItems:'center', gap:.5, mb:.5 }}>
                      <Typography fontSize={11} fontWeight={700} sx={{ color: m.color }}>{m.label}</Typography>
                      {m.note && <Chip label={m.note} size="small" sx={{ height:14, fontSize:8, bgcolor:'transparent', border:`1px solid ${m.color}44`, color: m.color, px:.5 }} />}
                    </Box>
                    <Typography fontSize={13} fontWeight={800} sx={{ color: m.color }}>
                      ₹{parseFloat(t.collected||0).toLocaleString('en-IN')}
                    </Typography>
                    <Typography fontSize={10} color="text.secondary">
                      {t.count} payments
                      {parseFloat(t.pending||0) > 0 && ` · ₹${parseFloat(t.pending).toLocaleString('en-IN')} pending`}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Breakdown section ────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius:1, boxShadow:'0 4px 20px rgba(0,0,0,0.08)', overflow:'hidden' }}>

        {/* Compact pill tab row */}
        <Box sx={{ px:2, py:1.5, borderBottom:'1px solid #f1f5f9',
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Typography fontSize={12} fontWeight={700} color="#64748b" sx={{ textTransform:'uppercase', letterSpacing:.6 }}>
            Payment Breakdown
          </Typography>
          <Box sx={{ display:'flex', gap:.75 }}>
            {[
              { id:'vendor',        label:'By Vendor',        icon: BadgeOutlined,  count: vendorBreakdown.length,  accent:'#ff6f00' },
              { id:'vendorpayment', label:'Vendor Payments',  icon: Payment,        count: vendorPayments.length,   accent:'#16a34a' },
              { id:'user',          label:'By User',          icon: People,         count: userBreakdown.length,    accent:'#1565c0' },
              { id:'vehicle',       label:'By Vehicle',       icon: DirectionsCar,  count: vehicleBreakdown.length, accent:'#7c3aed' },
            ].map(({ id, label, icon: Icon, count, accent }) => {
              const active = activeTab === id;
              return (
                <Box key={id} onClick={() => setActiveTab(id)} sx={{
                  display:'flex', alignItems:'center', gap:.6,
                  px:1.2, py:.5, borderRadius:99, cursor:'pointer',
                  bgcolor: active ? accent : '#f1f5f9',
                  border: `1px solid ${active ? accent : 'transparent'}`,
                  transition:'all .15s',
                  '&:hover':{ bgcolor: active ? accent : accent + '18' },
                }}>
                  <Icon sx={{ fontSize:13, color: active ? '#fff' : '#64748b' }} />
                  <Typography fontSize={11} fontWeight={600}
                    sx={{ color: active ? '#fff' : '#64748b' }}>{label}</Typography>
                  <Box sx={{ bgcolor: active ? 'rgba(255,255,255,.3)' : '#cbd5e1',
                    borderRadius:99, minWidth:16, height:16, display:'flex',
                    alignItems:'center', justifyContent:'center', px:.5 }}>
                    <Typography fontSize={9} fontWeight={700}
                      sx={{ color: active ? '#fff' : '#475569' }}>{count}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ── VENDOR TABLE ─────────────────────────────────────────────────── */}
        {activeTab === 'vendor' && (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['Vendor', 'Vehicles', 'Revenue', 'Advance Held', 'Pending', 'Overdue', 'Total'].map((h, i) => (
                    <TableCell key={h} align={i > 1 ? 'right' : 'left'} sx={{
                      fontWeight:700, fontSize:11, color:'#475569',
                      bgcolor:'#f8fafc', borderBottom:'2px solid #e2e8f0',
                      py:1.2, textTransform:'uppercase', letterSpacing:0.5,
                    }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {vendorBreakdown.length === 0
                  ? <TableRow><TableCell colSpan={7}><Empty icon={BadgeOutlined} msg="No vendor payment data yet" /></TableCell></TableRow>
                  : vendorBreakdown.map((v, i) => {
                    const total = parseFloat(v.collected||0)+parseFloat(v.pending||0)+parseFloat(v.overdue||0);
                    return (
                      <TableRow key={v.vendor_id} hover sx={{ bgcolor: i%2===0?'#fff':'#fafbff',
                        '&:hover':{ bgcolor:'#fff7ed' } }}>
                        <TableCell>
                          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                            <Avatar sx={{ width:34, height:34, bgcolor:'#ff6f00',
                              fontSize:13, fontWeight:800 }}>
                              {(v.vendor_name||'V')[0].toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography fontSize={13} fontWeight={700} color="#1e293b">
                                {v.vendor_name}
                              </Typography>
                              <Typography fontSize={10} color="text.secondary">
                                ID #{v.vendor_id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={`${v.total_vehicles} vehicles`} size="small"
                            icon={<DirectionsCar sx={{ fontSize:13 }} />}
                            sx={{ bgcolor:'#fff7ed', color:'#c2410c', fontWeight:600, fontSize:10 }} />
                        </TableCell>
                        <TableCell align="right"><AmtBadge val={v.collected}    color="#15803d" bg="#f0fdf4" /></TableCell>
                        <TableCell align="right"><AmtBadge val={v.advance_held||0} color="#0369a1" bg="#f0f9ff" /></TableCell>
                        <TableCell align="right"><AmtBadge val={v.pending}      color="#c2410c" bg="#fff7ed" /></TableCell>
                        <TableCell align="right"><AmtBadge val={v.overdue}      color="#dc2626" bg="#fef2f2" /></TableCell>
                        <TableCell align="right">
                          <Box sx={{ display:'inline-flex', alignItems:'center', bgcolor:'#1a237e',
                            px:1.5, py:0.4, borderRadius:1, minWidth:90, justifyContent:'flex-end' }}>
                            <Typography fontSize={12} fontWeight={800} color="#fff">
                              ₹{total.toLocaleString('en-IN')}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                }
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── VENDOR PAYMENTS TABLE (company → vendor) ─────────────────── */}
        {activeTab === 'vendorpayment' && (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['Vendor', 'Plans', 'Payments', 'Paid to Vendor', 'Pending', 'Overdue', 'Last Payment'].map((h, i) => (
                    <TableCell key={h} align={i > 2 ? 'right' : 'left'} sx={{
                      fontWeight:700, fontSize:11, color:'#475569',
                      bgcolor:'#f8fafc', borderBottom:'2px solid #e2e8f0',
                      py:1.2, textTransform:'uppercase', letterSpacing:0.5,
                    }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {vendorPayments.length === 0
                  ? <TableRow><TableCell colSpan={7}><Empty icon={Payment} msg="No vendor payments recorded yet" /></TableCell></TableRow>
                  : vendorPayments.map((v, i) => (
                    <TableRow key={v.vendor_id} hover sx={{ bgcolor: i%2===0?'#fff':'#f0fdf4', '&:hover':{ bgcolor:'#dcfce7' } }}>
                      <TableCell>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                          <Avatar sx={{ width:34, height:34, bgcolor:'#16a34a', fontSize:13, fontWeight:800 }}>
                            {(v.vendor_name||'V')[0].toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography fontSize={13} fontWeight={700} color="#1e293b">{v.vendor_name}</Typography>
                            <Typography fontSize={10} color="text.secondary">ID #{v.vendor_id}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={`${v.total_plans || 0} plans`} size="small"
                          sx={{ bgcolor:'#dcfce7', color:'#15803d', fontWeight:600, fontSize:10 }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={`${v.payment_count || 0} payments`} size="small"
                          sx={{ bgcolor:'#f0fdf4', color:'#166534', fontWeight:600, fontSize:10 }} />
                      </TableCell>
                      <TableCell align="right"><AmtBadge val={v.paid}    color="#15803d" bg="#f0fdf4" /></TableCell>
                      <TableCell align="right"><AmtBadge val={v.pending} color="#c2410c" bg="#fff7ed" /></TableCell>
                      <TableCell align="right"><AmtBadge val={v.overdue} color="#dc2626" bg="#fef2f2" /></TableCell>
                      <TableCell align="right">
                        <Typography fontSize={11} color="text.secondary">
                          {v.last_payment_date
                            ? new Date(v.last_payment_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
                            : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── USER TABLE ───────────────────────────────────────────────────── */}
        {activeTab === 'user' && (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['User','Vehicle','Plan','Revenue','Advance','Pending','Overdue','Total'].map((h,i) => (
                    <TableCell key={h} align={i>2?'right':'left'} sx={{
                      fontWeight:700, fontSize:11, color:'#475569',
                      bgcolor:'#f8fafc', borderBottom:'2px solid #e2e8f0',
                      py:1.2, textTransform:'uppercase', letterSpacing:0.5,
                    }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {userBreakdown.length === 0
                  ? <TableRow><TableCell colSpan={8}><Empty icon={People} msg="No user payment data yet" /></TableCell></TableRow>
                  : userBreakdown.map((u, i) => {
                    const total = parseFloat(u.collected||0)+parseFloat(u.pending||0)+parseFloat(u.overdue||0);
                    return (
                      <TableRow key={`${u.user_id}-${i}`} hover sx={{ bgcolor: i%2===0?'#fff':'#fafbff',
                        '&:hover':{ bgcolor:'#eff6ff' } }}>
                        <TableCell>
                          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                            <Avatar sx={{ width:34, height:34,
                              background:'linear-gradient(135deg,#1a237e,#1565c0)',
                              fontSize:13, fontWeight:800 }}>
                              {(u.user_name||'U')[0].toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography fontSize={13} fontWeight={700} color="#1e293b">{u.user_name}</Typography>
                              <Typography fontSize={10} color="text.secondary">{u.user_phone}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {u.vehicle_number
                            ? <Chip label={u.vehicle_number} size="small"
                                sx={{ fontFamily:'monospace', fontWeight:700, fontSize:10, bgcolor:'#f5f3ff', color:'#7c3aed' }} />
                            : <Typography fontSize={11} color="text.secondary">—</Typography>
                          }
                        </TableCell>
                        <TableCell>
                          {u.frequency && u.plan_amount
                            ? <Box sx={{ bgcolor:'#fefce8', border:'1px solid #fde047', px:1, py:0.3, borderRadius:1, display:'inline-block' }}>
                                <Typography fontSize={11} fontWeight={700} color="#a16207">
                                  ₹{parseFloat(u.plan_amount).toLocaleString('en-IN')}/{u.frequency}
                                </Typography>
                              </Box>
                            : <Typography fontSize={11} color="text.secondary">—</Typography>
                          }
                        </TableCell>
                        <TableCell align="right"><AmtBadge val={u.collected}             color="#15803d" bg="#f0fdf4" /></TableCell>
                        <TableCell align="right"><AmtBadge val={u.advance_paid||0}       color="#0369a1" bg="#f0f9ff" /></TableCell>
                        <TableCell align="right"><AmtBadge val={u.pending}               color="#c2410c" bg="#fff7ed" /></TableCell>
                        <TableCell align="right"><AmtBadge val={u.overdue}               color="#dc2626" bg="#fef2f2" /></TableCell>
                        <TableCell align="right">
                          <Box sx={{ display:'inline-flex', alignItems:'center', bgcolor:'#1a237e',
                            px:1.5, py:0.4, borderRadius:1, minWidth:90, justifyContent:'flex-end' }}>
                            <Typography fontSize={12} fontWeight={800} color="#fff">
                              ₹{total.toLocaleString('en-IN')}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                }
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── VEHICLE TABLE ────────────────────────────────────────────────── */}
        {activeTab === 'vehicle' && (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['Vehicle','Vendor','Assigned To','Revenue','Advance','Pending','Overdue','Total'].map((h,i) => (
                    <TableCell key={h} align={i>2?'right':'left'} sx={{
                      fontWeight:700, fontSize:11, color:'#475569',
                      bgcolor:'#f8fafc', borderBottom:'2px solid #e2e8f0',
                      py:1.2, textTransform:'uppercase', letterSpacing:0.5,
                    }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicleBreakdown.length === 0
                  ? <TableRow><TableCell colSpan={8}><Empty icon={DirectionsCar} msg="No vehicle payment data yet" /></TableCell></TableRow>
                  : vehicleBreakdown.map((v, i) => {
                    const total = parseFloat(v.collected||0)+parseFloat(v.pending||0)+parseFloat(v.overdue||0);
                    return (
                      <TableRow key={v.vehicle_id} hover sx={{ bgcolor: i%2===0?'#fff':'#fafbff',
                        '&:hover':{ bgcolor:'#faf5ff' } }}>
                        <TableCell>
                          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                            <Avatar sx={{ width:34, height:34, bgcolor:'#7c3aed', fontSize:13, fontWeight:800 }}>
                              <DirectionsCar sx={{ fontSize:18 }} />
                            </Avatar>
                            <Box>
                              <Typography fontSize={13} fontWeight={700} sx={{ fontFamily:'monospace', color:'#1e293b' }}>
                                {v.vehicle_number}
                              </Typography>
                              <Typography fontSize={10} color="text.secondary">{v.model || '—'}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {v.vendor_name
                            ? <Chip label={v.vendor_name} size="small" sx={{ bgcolor:'#fff7ed', color:'#c2410c', fontWeight:600, fontSize:10 }} />
                            : <Typography fontSize={11} color="text.secondary">—</Typography>
                          }
                        </TableCell>
                        <TableCell>
                          {v.assigned_user
                            ? <Box sx={{ display:'flex', alignItems:'center', gap:0.8 }}>
                                <Avatar sx={{ width:22, height:22, fontSize:10, fontWeight:700, bgcolor:'#e0e7ff', color:'#4338ca' }}>
                                  {v.assigned_user[0].toUpperCase()}
                                </Avatar>
                                <Typography fontSize={11} fontWeight={600} color="#1e293b">{v.assigned_user}</Typography>
                              </Box>
                            : <Typography fontSize={11} color="text.secondary">—</Typography>
                          }
                        </TableCell>
                        <TableCell align="right"><AmtBadge val={v.collected}         color="#15803d" bg="#f0fdf4" /></TableCell>
                        <TableCell align="right"><AmtBadge val={v.advance_held||0}   color="#0369a1" bg="#f0f9ff" /></TableCell>
                        <TableCell align="right"><AmtBadge val={v.pending}           color="#c2410c" bg="#fff7ed" /></TableCell>
                        <TableCell align="right"><AmtBadge val={v.overdue}           color="#dc2626" bg="#fef2f2" /></TableCell>
                        <TableCell align="right">
                          <Box sx={{ display:'inline-flex', alignItems:'center', bgcolor:'#1a237e',
                            px:1.5, py:0.4, borderRadius:1, minWidth:90, justifyContent:'flex-end' }}>
                            <Typography fontSize={12} fontWeight={800} color="#fff">
                              ₹{total.toLocaleString('en-IN')}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                }
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isVendor = user?.role?.toLowerCase() === 'vendor';

  if (isVendor) return <VendorDashboard user={user} />;

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleFilter, setVehicleFilter] = useState('all'); // all, active, assigned, maintenance
  const [loadingVehicles, setLoadingVehicles] = useState(true);

  const loadStats = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    const res = await InvokeApi(Config.base_url + apiList.getDashboardStats, {});
    if (res?.code === '200') setStats(res.stats ?? {});
    setLoading(false); setRefreshing(false);
  }, []);

  const loadVehicles = useCallback(async () => {
    setLoadingVehicles(true);
    // Use Loconav live telematics for the map — real GPS positions
    const res = await InvokeApi(Config.base_url + apiList.loconavTelematics, { page: 1, per_page: 100 });
    if (res?.code === '200') {
      // Map Loconav telematics to the shape VehicleMap expects
      const mapped = (res.telematics || []).map(t => ({
        vehicle_number: t.vehicle_number,
        latitude:       t.lat,
        longitude:      t.lng,
        speed:          t.speed,
        status:         t.movement === 'MOVING' ? 'active' : 'assigned',
        is_online:      true,
        current_user:   null,
      }));
      setVehicles(mapped);
    }
    setLoadingVehicles(false);
  }, []);

  useEffect(() => { 
    loadStats(); 
    loadVehicles();
  }, [loadStats, loadVehicles]);

  const reg = stats?.registrations_chart ?? [];
  const comp = stats?.complaints_chart ?? [];
  const recentKYC = stats?.recent_kyc ?? [];
  const recentAssign = stats?.recent_assignments ?? [];

  // Vehicle status pie data
  const vehiclePie = [
    { name: 'Available', value: (stats?.total_vehicles ?? 0) - (stats?.assigned_vehicles ?? 0) - (stats?.maintenance_vehicles ?? 0) },
    { name: 'Assigned', value: stats?.assigned_vehicles ?? 0 },
    { name: 'Maintenance', value: stats?.maintenance_vehicles ?? 0 },
    { name: 'Retired', value: stats?.retired_vehicles ?? 0 },
  ].filter(d => d.value > 0);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <Box>
      {/* ── Welcome banner ─────────────────────────────────────────────────── */}
      <Box sx={{
        mb: 3, p: 3, borderRadius: 1,
        background: 'linear-gradient(135deg, #1a237e 0%, #1565c0 60%, #00acc1 100%)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2,
      }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#fff">
            {greeting}, {user?.name || 'Admin'} 👋
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.3 }}>
            Here's what's happening with Ridexcity today.
          </Typography>
        </Box>
        <Tooltip title={refreshing ? 'Refreshing…' : 'Refresh stats'}>
          <IconButton
            onClick={() => loadStats(true)}
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
          >
            <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Stat cards ──────────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { key: 'total_users', label: 'Total Users', Icon: People, color: '#1a237e' },
          { key: 'total_vehicles', label: 'Total Vehicles', Icon: ElectricScooter, color: '#1565c0' },
          { key: 'assigned_vehicles', label: 'Assigned Vehicles', Icon: AssignmentTurnedIn, color: '#16a34a' },
          { key: 'kyc_pending', label: 'KYC Pending', Icon: VerifiedUser, color: '#d97706' },
          { key: 'open_complaints', label: 'Open Complaints', Icon: ReportProblem, color: '#dc2626' },
          { key: 'open_tickets', label: 'Open Tickets', Icon: ConfirmationNumber, color: '#7c3aed' },
          { key: 'total_stations', label: 'Battery Stations', Icon: BatteryChargingFull, color: '#0891b2' },
          { key: 'total_employees', label: 'Total Employees', Icon: BadgeOutlined, color: '#64748b' },
        ].map(({ key, label, Icon, color }) => (
          <Grid item xs={6} sm={4} md={3} key={key}>
            <StatCard
              label={label}
              value={stats?.[key]}
              icon={Icon}
              color={color}
              loading={loading}
            />
          </Grid>))}
      </Grid>

      {/* ── Charts row ──────────────────────────────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Registrations — daily area chart (investment style) */}
        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 2.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <SectionHeader title="User Registrations" icon={TrendingUp} />
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>Last 10 days</Typography>
            </Box>
            {loading
              ? <Skeleton variant="rectangular" height={210} sx={{ borderRadius: 2 }} />
              : reg.length === 0
                ? <Box sx={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary" variant="body2">No registrations in last 10 days</Typography>
                  </Box>
                : <>
                    {/* Summary row */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                      <Box>
                        <Typography variant="h5" fontWeight={800} color="#1a237e" lineHeight={1}>
                          {reg.reduce((s, d) => s + (d.users || 0), 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Total registrations</Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight={800} color="#16a34a" lineHeight={1}>
                          {reg[reg.length - 1]?.users ?? 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Today</Typography>
                      </Box>
                    </Box>
                    <ResponsiveContainer width="100%" height={165}>
                      <AreaChart data={reg} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                        <defs>
                          <linearGradient id="regGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#1a237e" stopOpacity={0.18} />
                            <stop offset="95%" stopColor="#1a237e" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                        <ReTooltip
                          contentStyle={{ borderRadius: 8, fontSize: 12, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
                          formatter={(v) => [v, 'Registrations']}
                        />
                        <Area
                          type="monotone" dataKey="users"
                          stroke="#1a237e" strokeWidth={2.5}
                          fill="url(#regGrad)" dot={false}
                          activeDot={{ r: 5, fill: '#1a237e', stroke: '#fff', strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </>
            }
          </Card>
        </Grid>

        {/* Complaints — daily bar chart filed vs resolved */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 2.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <SectionHeader title="Complaints" icon={ReportProblem} />
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>Last 10 days</Typography>
            </Box>
            {loading
              ? <Skeleton variant="rectangular" height={210} sx={{ borderRadius: 2 }} />
              : comp.length === 0
                ? <Box sx={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary" variant="body2">No complaints in last 10 days</Typography>
                  </Box>
                : <>
                    {/* Summary row */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                      <Box>
                        <Typography variant="h5" fontWeight={800} color="#dc2626" lineHeight={1}>
                          {comp.reduce((s, d) => s + (d.filed || 0), 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Filed</Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight={800} color="#16a34a" lineHeight={1}>
                          {comp.reduce((s, d) => s + (parseInt(d.resolved) || 0), 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Resolved</Typography>
                      </Box>
                    </Box>
                    <ResponsiveContainer width="100%" height={165}>
                      <BarChart data={comp} margin={{ top: 4, right: 4, bottom: 0, left: -20 }} barSize={10}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                        <ReTooltip
                          contentStyle={{ borderRadius: 8, fontSize: 12, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}
                        />
                        <Legend wrapperStyle={{ fontSize: 10, paddingTop: 4 }} />
                        <Bar dataKey="filed"    name="Filed"    fill="#fca5a5" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="resolved" name="Resolved" fill="#86efac" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </>
            }
          </Card>
        </Grid>

        {/* Vehicle status pie */}
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 2.5, height: '100%' }}>
            <SectionHeader title="Vehicle Status" icon={DirectionsCar} />
            {loading
              ? <Skeleton variant="rectangular" height={210} sx={{ borderRadius: 2 }} />
              : vehiclePie.length === 0
                ? <Box sx={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary" variant="body2">No vehicles</Typography>
                </Box>
                : <>
                  <ResponsiveContainer width="100%" height={140}>
                    <PieChart>
                      <Pie data={vehiclePie} cx="50%" cy="50%" innerRadius={40} outerRadius={65}
                        paddingAngle={3} dataKey="value">
                        {vehiclePie.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <ReTooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ mt: 1 }}>
                    {vehiclePie.map((d, i) => (
                      <Box key={d.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <Typography variant="caption" color="text.secondary">{d.name}</Typography>
                        </Box>
                        <Typography variant="caption" fontWeight={700}>{d.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
            }
          </Card>
        </Grid>
      </Grid>

      {/* ── Bottom row: KYC + Activity ───────────────────────────────────────── */}
      <Grid container spacing={2.5}>
        {/* Recent KYC */}
        <Grid item xs={12} lg={7}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
              <SectionHeader title="Recent KYC Submissions" icon={VerifiedUser} />
            </Box>
            {loading ? (
              <Box sx={{ px: 2.5, pb: 2 }}>
                {[...Array(4)].map((_, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                    <Skeleton variant="circular" width={36} height={36} />
                    <Box sx={{ flex: 1 }}><Skeleton width="60%" /><Skeleton width="40%" height={12} /></Box>
                    <Skeleton width={70} height={24} sx={{ borderRadius: 99 }} />
                  </Box>
                ))}
              </Box>
            ) : recentKYC.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No recent KYC submissions</Typography>
              </Box>
            ) : (
              <Box>
                {recentKYC.map((k, i) => (
                  <Box key={k.id ?? i}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.5 }}>
                      <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 14, fontWeight: 700 }}>
                        {(k.name || k.user_name || 'U')[0].toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{k.name || k.user_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {k.phone || k.user_phone || '—'}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Chip label={k.status || k.kyc_status || 'pending'} color={KYC_COLOR[k.status || k.kyc_status] || 'default'} size="small" sx={{ fontWeight: 600 }} />
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.3 }}>
                          {k.submitted_at ? new Date(k.submitted_at).toLocaleDateString() : '—'}
                        </Typography>
                      </Box>
                    </Box>
                    {i < recentKYC.length - 1 && <Divider sx={{ mx: 2.5 }} />}
                  </Box>
                ))}
              </Box>
            )}
          </Card>
        </Grid>

        {/* Recent vehicle activity */}
        <Grid item xs={12} lg={5}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
              <SectionHeader title="Recent Vehicle Activity" icon={SwapHoriz} />
            </Box>
            {loading ? (
              <Box sx={{ px: 2.5, pb: 2 }}>
                {[...Array(4)].map((_, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                    <Skeleton variant="circular" width={36} height={36} />
                    <Box sx={{ flex: 1 }}><Skeleton width="55%" /><Skeleton width="40%" height={12} /></Box>
                  </Box>
                ))}
              </Box>
            ) : recentAssign.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No recent vehicle activity</Typography>
              </Box>
            ) : (
              <Box>
                {recentAssign.map((a, i) => {
                  const isAssigned = a.event_type === 'assigned';
                  return (
                    <Box key={i}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.5 }}>
                        <Avatar sx={{
                          width: 36, height: 36, fontSize: 14, flexShrink: 0,
                          bgcolor: isAssigned ? '#dcfce7' : '#fef3c7',
                        }}>
                          {isAssigned
                            ? <AssignmentTurnedIn sx={{ fontSize: 18, color: '#16a34a' }} />
                            : <SwapHoriz sx={{ fontSize: 18, color: '#d97706' }} />}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.2 }}>
                            <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }} noWrap>
                              {a.vehicle_number}
                            </Typography>
                            <Chip
                              label={isAssigned ? 'Assigned' : 'Returned'}
                              color={isAssigned ? 'success' : 'warning'}
                              size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {a.user_name} · by {a.by_name}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, textAlign: 'right' }}>
                          {a.date}
                          {a.time && <><br />{a.time}</>}
                        </Typography>
                      </Box>
                      {i < recentAssign.length - 1 && <Divider sx={{ mx: 2.5 }} />}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* ── Payment Dashboard (Admin/Superadmin only) ───────────────────────── */}
      {(user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'superadmin') && (
        <AdminPaymentSection />
      )}

      {/* ── Live Vehicle Map (Loconav) ──────────────────────────────────────── */}
      <Box sx={{ mt: 3 }}>
        <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <Box sx={{
            px: 2.5, pt: 2.5, pb: 1,
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', flexWrap: 'wrap', gap: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SectionHeader title="Live Vehicle Locations" icon={Map} />
              {/* <Chip label="Loconav" size="small" sx={{ bgcolor: '#e8f5e9', color: '#16a34a', fontWeight: 700, fontSize: 10, height: 18 }} /> */}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={vehicleFilter}
                  label="Filter by Status"
                  onChange={(e) => setVehicleFilter(e.target.value)}
                >
                  <MenuItem value="all">All Vehicles</MenuItem>
                  <MenuItem value="active">Moving</MenuItem>
                  <MenuItem value="assigned">Stopped</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Refresh map">
                <IconButton size="small" onClick={loadVehicles} sx={{ border: '1px solid #e8eaf6', borderRadius: 1.5 }}>
                  <Refresh sx={{ fontSize: 18, color: '#1a237e' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Divider />
          {loadingVehicles ? (
            <Skeleton variant="rectangular" height={500} />
          ) : (
            <VehicleMap vehicles={vehicles} filter={vehicleFilter} />
          )}
        </Card>
      </Box>

      {/* Spin keyframe for refresh icon */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
}

// ── Vendor Dashboard Component ───────────────────────────────────────────────
function VendorDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [recentVehicles, setRecentVehicles] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mapVehicles, setMapVehicles] = useState([]);
  const [loadingMap, setLoadingMap] = useState(true);
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [paymentSummary, setPaymentSummary] = useState(null);
  const navigate = useNavigate();

  const loadDashboard = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    const [dashRes, payRes] = await Promise.all([
      InvokeApi(Config.base_url + apiList.getVendorDashboard, {}),
      InvokeApi(Config.base_url + apiList.getVendorPaymentSummary, {}),
    ]);
    if (dashRes?.code === '200') {
      setStats(dashRes.stats ?? {});
      setRecentVehicles(dashRes.recent_vehicles ?? []);
      setRecentPayments(dashRes.recent_payments ?? []);
    }
    if (payRes?.code === '200') setPaymentSummary(payRes);
    setLoading(false); setRefreshing(false);
  }, []);

  // Load map — vendor-scoped telematics (backend filters to this vendor's vehicles only)
  const loadMapVehicles = useCallback(async () => {
    setLoadingMap(true);
    try {
      const res = await InvokeApi(Config.base_url + apiList.loconavVendorTelematics, {});
      if (res?.code === '200' && res.telematics?.length > 0) {
        const mapped = res.telematics.map(t => ({
          vehicle_number: t.vehicle_number,
          latitude:       t.lat   != null ? parseFloat(t.lat)   : null,
          longitude:      t.lng   != null ? parseFloat(t.lng)   : null,
          speed:          t.speed ?? 0,
          status:         t.movement === 'MOVING' ? 'active' : 'assigned',
          is_online:      true,
        })).filter(v => v.latitude && v.longitude); // only vehicles with valid GPS
        setMapVehicles(mapped);
      } else {
        console.warn('[VendorMap] vendor-telematics response:', res);
        setMapVehicles([]);
      }
    } catch (e) {
      console.error('[VendorMap] error:', e);
    } finally {
      setLoadingMap(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    loadMapVehicles();
  }, [loadDashboard, loadMapVehicles]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress sx={{ color: '#1a237e' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Welcome banner ─────────────────────────────────────────────────── */}
      <Box sx={{
        mb: 3, p: 3, borderRadius: 1,
        background: 'linear-gradient(135deg, #ff6f00 0%, #ff8f00 60%, #ffa726 100%)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2,
      }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#fff">
            {greeting}, {user?.name || 'Vendor'} 👋
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.3 }}>
            Your {user?.name || 'Vendor'} dashboard for today.
          </Typography>
        </Box>
        <Tooltip title={refreshing ? 'Refreshing…' : 'Refresh stats'}>
          <IconButton
            onClick={() => loadDashboard(true)}
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
          >
            <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Vehicle Stats ───────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard 
            label="Total Vehicles" 
            value={stats?.total_vehicles ?? 0} 
            icon={ElectricScooter} 
            color="#ff6f00" 
            loading={false}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard 
            label="Assigned" 
            value={stats?.assigned_vehicles ?? 0} 
            icon={AssignmentTurnedIn} 
            color="#1565c0" 
            loading={false}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard 
            label="Available" 
            value={stats?.available_vehicles ?? 0} 
            icon={BatteryChargingFull} 
            color="#16a34a" 
            loading={false}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard 
            label="Maintenance" 
            value={stats?.maintenance_vehicles ?? 0} 
            icon={Build} 
            color="#d97706" 
            loading={false}
          />
        </Grid>
      </Grid>

      {/* ── Payment Stats ───────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Received', val: paymentSummary?.total_paid,   color: '#2e7d32', bg: '#f0fdf4', border: '#bbf7d0', icon: TrendingUp },
          { label: 'Pending',        val: paymentSummary?.total_pending, color: '#c2410c', bg: '#fff7ed', border: '#fed7aa', icon: ConfirmationNumber },
          { label: 'Overdue',        val: paymentSummary?.total_overdue, color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: ReportProblem },
        ].map(({ label, val, color, bg, border, icon: Icon }) => (
          <Grid item xs={12} sm={4} key={label}>
            <Card sx={{ borderRadius: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1px solid ${border}`, bgcolor: bg }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: '#fff', border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ fontSize: 20, color }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={800} fontSize={17} sx={{ color, lineHeight: 1.2 }}>
                      ₹{parseFloat(val || 0).toLocaleString('en-IN')}
                    </Typography>
                    <Typography fontSize={11} color="text.secondary" fontWeight={500}>{label}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Bottom row: Recent Vehicles + Recent Payments ───────────────────── */}
      <Grid container spacing={2.5}>
        {/* Recent Vehicles */}
        <Grid item xs={12} lg={7}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <SectionHeader title="Recent Vehicles" icon={DirectionsCar} />
              <Button size="small" onClick={() => navigate('/vehicles')} sx={{ color: '#1a237e', fontWeight: 600 }}>View All</Button>
            </Box>
            {recentVehicles.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No vehicles assigned</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: '#f5f6fa' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Vehicle</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Model</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Status</TableCell>
                      {/* <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Battery</TableCell> */}
                      {/* <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Assigned To</TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentVehicles.map((v) => (
                      <TableRow key={v.id} hover onClick={() => navigate(`/vehicle/${v.id}`)} sx={{ cursor: 'pointer' }}>
                        <TableCell><Typography fontWeight={700} fontSize={12} sx={{ fontFamily: 'monospace' }}>{v.vehicle_number}</Typography></TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{v.model || '—'}</TableCell>
                        <TableCell><Chip label={v.status || 'active'} size="small" color={STATUS_COLOR[v.status] || 'default'} sx={{ fontWeight: 600, fontSize: 10 }} /></TableCell>
                        {/* <TableCell><Typography variant="caption" sx={{ fontWeight: 700, color: battColor(v.battery_pct) }}>{v.battery_pct ?? 0}%</Typography></TableCell> */}
                        {/* <TableCell>
                          {v.assigned_user
                            ? <Box><Typography fontSize={11} fontWeight={600}>{v.assigned_user}</Typography><Typography fontSize={10} color="text.secondary">{v.assigned_phone}</Typography></Box>
                            : <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell> */}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Grid>

        {/* Recent Payments received from company */}
        <Grid item xs={12} lg={5}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
              <SectionHeader title="Recent Payments" icon={ConfirmationNumber} />
            </Box>
            {!paymentSummary || !paymentSummary.recent_payments?.length ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No recent payments</Typography>
              </Box>
            ) : (
              <Box>
                {paymentSummary.recent_payments.map((p, i) => (
                  <Box key={p.id ?? i}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.5 }}>
                      <Avatar sx={{ width: 36, height: 36, flexShrink: 0, bgcolor: p.status === 'paid' ? '#dcfce7' : '#fef3c7' }}>
                        {p.status === 'paid' ? <TrendingUp sx={{ fontSize: 18, color: '#16a34a' }} /> : <ConfirmationNumber sx={{ fontSize: 18, color: '#d97706' }} />}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.2 }}>
                          <Typography variant="body2" fontWeight={700}>₹{parseFloat(p.amount ?? 0).toLocaleString('en-IN')}</Typography>
                          <Chip label={p.status || 'pending'} size="small"
                            color={p.status === 'paid' ? 'success' : p.status === 'overdue' ? 'error' : 'warning'}
                            sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {p.collected_by_name ? `By: ${p.collected_by_name}` : '—'}
                          {p.notes ? ` · ${p.notes}` : ''}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {p.paid_date ? fmtDate(p.paid_date) : '—'}
                      </Typography>
                    </Box>
                    {i < paymentSummary.recent_payments.length - 1 && <Divider sx={{ mx: 2.5 }} />}
                  </Box>
                ))}
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* ── Live Vehicle Map — same Google Maps component as admin ────────── */}
      <Box sx={{ mt: 3 }}>
        <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <Box sx={{
            px: 2.5, pt: 2.5, pb: 1,
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', flexWrap: 'wrap', gap: 2,
          }}>
            <SectionHeader title="Live Vehicle Locations" icon={Map} />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Filter by Status</InputLabel>
                <Select value={vehicleFilter} label="Filter by Status"
                  onChange={e => setVehicleFilter(e.target.value)}>
                  <MenuItem value="all">All Vehicles</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="assigned">Assigned</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={loadMapVehicles} sx={{ border: '1px solid #e8eaf6', borderRadius: 1.5 }}>
                  <Refresh sx={{ fontSize: 18, color: '#1a237e' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Divider />
          {loadingMap ? (
            <Skeleton variant="rectangular" height={500} />
          ) : (
            <VehicleMap vehicles={mapVehicles} filter={vehicleFilter} />
          )}
        </Card>
      </Box>

      {/* Spin keyframe for refresh icon */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
}

// ── Vendor Leaflet Map — simple, no timing issues ────────────────────────────
const VendorLeafletMap = ({ vehicles, loading }) => {
  const leafletRef    = React.useRef(null);
  const vehiclesRef   = React.useRef(vehicles);
  vehiclesRef.current = vehicles; // always current without re-running effect

  // Callback ref — fires when the DOM node mounts/unmounts
  const mapCallback = React.useCallback((node) => {
    if (!node) {
      // Node unmounted — clean up
      if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; }
      return;
    }

    const initMap = () => {
      const L = window.L;
      if (!L) return;
      if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; }

      const valid = vehiclesRef.current.filter(v => v.latitude && v.longitude);
      const center = valid.length > 0
        ? [parseFloat(valid[0].latitude), parseFloat(valid[0].longitude)]
        : [12.97, 77.59];

      const map = L.map(node, { zoomControl: true }).setView(center, 12);
      leafletRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap', maxZoom: 19,
      }).addTo(map);

      const bounds = [];
      valid.forEach(v => {
        const lat = parseFloat(v.latitude);
        const lng = parseFloat(v.longitude);
        const isMoving = v.status === 'active';
        const color = isMoving ? '#16a34a' : '#1565c0';
        const icon = L.divIcon({
          html: `<div style="background:${color};color:#fff;padding:3px 6px;border-radius:8px;font-size:10px;font-weight:700;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.3);border:2px solid #fff">${v.vehicle_number}</div>`,
          className: '', iconAnchor: [0, 0],
        });
        L.marker([lat, lng], { icon }).addTo(map)
          .bindPopup(`<b>${v.vehicle_number}</b><br>${isMoving ? '🟢 Moving' : '🔵 Stopped'}`);
        bounds.push([lat, lng]);
      });

      if (bounds.length > 1) map.fitBounds(bounds, { padding: [40, 40] });
      else if (bounds.length === 1) map.setView(bounds[0], 14);
    };

    // Load Leaflet CSS if needed
    if (!document.getElementById('lf-css')) {
      const l = document.createElement('link'); l.id = 'lf-css'; l.rel = 'stylesheet';
      l.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(l);
    }

    if (window.L) {
      initMap();
    } else {
      let s = document.getElementById('lf-js');
      if (!s) {
        s = document.createElement('script'); s.id = 'lf-js';
        s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'; document.head.appendChild(s);
      }
      s.addEventListener('load', initMap, { once: true });
    }
  }, []); // empty deps — stable callback, uses vehiclesRef for current data

  // When vehicles change after initial mount, refresh markers
  React.useEffect(() => {
    const L = window.L;
    if (!leafletRef.current || loading || !L) return;

    const map = leafletRef.current;
    // Remove all existing markers
    map.eachLayer(layer => {
      if (layer instanceof L.Marker) map.removeLayer(layer);
    });

    const valid = vehicles.filter(v => v.latitude && v.longitude);
    const bounds = [];
    valid.forEach(v => {
      const lat = parseFloat(v.latitude);
      const lng = parseFloat(v.longitude);
      const isMoving = v.status === 'active';
      const color = isMoving ? '#16a34a' : '#1565c0';
      const icon = L.divIcon({
        html: `<div style="background:${color};color:#fff;padding:3px 6px;border-radius:8px;font-size:10px;font-weight:700;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.3);border:2px solid #fff">${v.vehicle_number}</div>`,
        className: '', iconAnchor: [0, 0],
      });
      L.marker([lat, lng], { icon }).addTo(map)
        .bindPopup(`<b>${v.vehicle_number}</b><br>${isMoving ? '🟢 Moving' : '🔵 Stopped'}`);
      bounds.push([lat, lng]);
    });

    if (bounds.length > 1) map.fitBounds(bounds, { padding: [40, 40] });
    else if (bounds.length === 1) map.setView(bounds[0], 14);
  }, [vehicles, loading]);

  const validCount = vehicles.filter(v => v.latitude && v.longitude).length;

  if (loading) {
    return (
      <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
        <CircularProgress sx={{ color: '#1a237e' }} />
      </Box>
    );
  }

  if (validCount === 0) {
    return (
      <Box sx={{ height: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', gap: 1 }}>
        <Typography color="text.secondary" fontWeight={600}>No GPS data available</Typography>
        <Typography fontSize={12} color="text.disabled">Vehicles may be offline</Typography>
      </Box>
    );
  }

  // Use callback ref — fires synchronously when DOM node mounts
  return <Box ref={mapCallback} sx={{ width: '100%', height: 400 }} />;
};

// ── Vehicle Map Component ─────────────────────────────────────────────────────
const VehicleMap = ({ vehicles, filter }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = React.useRef(null);
  const googleMapRef = React.useRef(null);
  const markersRef = React.useRef([]);

  // Filter vehicles based on selected filter (supports both DB status and Loconav movement)
  const filteredVehicles = React.useMemo(() => {
    if (filter === 'all') return vehicles;
    return vehicles.filter(v => {
      if (filter === 'active')      return v.status === 'active'      || v.movement === 'MOVING';
      if (filter === 'assigned')    return v.status === 'assigned'    || v.movement === 'STOPPED';
      if (filter === 'maintenance') return v.status === 'maintenance' || v.movement === 'IDLE';
      return true;
    });
  }, [vehicles, filter]);

  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps) {
      setMapLoaded(true);
      return;
    }

    const script = document.createElement('script');
    // TODO: Replace with your own Google Maps API key
    // Get it from: https://console.cloud.google.com/google/maps-apis/credentials
    const GOOGLE_MAPS_API_KEY = 'AIzaSyBEkY7qnLR8cl0P2_dhmy5ytsZTxCTG67M'; // Replace this
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Initialize map and markers
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.google) return;

    // Center on Bangalore
    const center = { lat: 12.9716, lng: 77.5946 };

    // Initialize map
    if (!googleMapRef.current) {
      googleMapRef.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 12,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });
    }

    // Clear existing markers
    markersRef.current.forEach(marker => {
      marker.setMap(null);
      if (marker.overlay) {
        marker.overlay.setMap(null);
      }
    });
    markersRef.current = [];

    // Add markers for filtered vehicles
    filteredVehicles.forEach(vehicle => {
      if (!vehicle.latitude || !vehicle.longitude) return;

      const position = {
        lat: parseFloat(vehicle.latitude),
        lng: parseFloat(vehicle.longitude)
      };

      // Determine marker color based on status
      let markerColor = '#1565c0'; // default blue for active
      let statusLabel = vehicle.status || 'active';
      
      if (vehicle.status === 'assigned') {
        markerColor = '#16a34a'; // green for assigned
      } else if (vehicle.status === 'maintenance') {
        markerColor = '#dc2626'; // red for maintenance
      } else if (vehicle.status === 'active') {
        markerColor = '#1565c0'; // blue for active
      }

      // Create custom marker with scooter/vehicle icon
      const svgMarker = {
        path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
        fillColor: markerColor,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1.8,
        anchor: new window.google.maps.Point(12, 22),
      };

      const marker = new window.google.maps.Marker({
        position,
        map: googleMapRef.current,
        title: vehicle.vehicle_number,
        icon: svgMarker,
        animation: window.google.maps.Animation.DROP,
      });

      // Create custom label overlay for vehicle number
      const labelDiv = document.createElement('div');
      labelDiv.style.cssText = `
        position: absolute;
        background: ${markerColor};
        color: white;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: bold;
        white-space: nowrap;
        border: 1px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        transform: translate(-50%, -100%);
        margin-top: -36px;
        pointer-events: none;
        z-index: 1000;
      `;
      labelDiv.textContent = vehicle.vehicle_number || '';

      class CustomOverlay extends window.google.maps.OverlayView {
        constructor(position, content) {
          super();
          this.position = position;
          this.content = content;
        }

        onAdd() {
          this.getPanes().floatPane.appendChild(this.content);
        }

        draw() {
          const projection = this.getProjection();
          const point = projection.fromLatLngToDivPixel(this.position);
          if (point) {
            this.content.style.left = point.x + 'px';
            this.content.style.top = point.y + 'px';
          }
        }

        onRemove() {
          if (this.content.parentElement) {
            this.content.parentElement.removeChild(this.content);
          }
        }
      }

      const overlay = new CustomOverlay(marker.getPosition(), labelDiv);
      overlay.setMap(googleMapRef.current);

      // Info window for vehicle details
      const infoContent = `
        <div style="padding: 8px; font-family: system-ui;">
          <div style="font-size: 14px; font-weight: bold; color: #1a237e; margin-bottom: 6px;">
            ${vehicle.vehicle_number}
          </div>
          <div style="font-size: 11px; color: #64748b; margin-bottom: 3px;">
            <strong>Status:</strong> <span style="color: ${markerColor}; font-weight: 600; text-transform: uppercase;">${statusLabel}</span>
          </div>
          ${vehicle.current_user ? `
            <div style="font-size: 11px; color: #64748b;">
              <strong>User:</strong> ${vehicle.current_user}
            </div>
          ` : ''}
        </div>
      `;

      const infoWindow = new window.google.maps.InfoWindow({
        content: infoContent
      });

      marker.addListener('click', () => {
        // Close all other info windows
        markersRef.current.forEach(m => {
          if (m.infoWindow) m.infoWindow.close();
        });
        infoWindow.open(googleMapRef.current, marker);
      });

      marker.infoWindow = infoWindow;
      marker.overlay = overlay;
      markersRef.current.push(marker);
    });

    // Adjust map bounds to show all markers
    if (filteredVehicles.length > 0 && markersRef.current.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      markersRef.current.forEach(marker => {
        bounds.extend(marker.getPosition());
      });
      googleMapRef.current.fitBounds(bounds);
      
      // Don't zoom in too much if there's only one marker
      const listener = window.google.maps.event.addListener(googleMapRef.current, 'idle', () => {
        if (googleMapRef.current.getZoom() > 15) {
          googleMapRef.current.setZoom(15);
        }
        window.google.maps.event.removeListener(listener);
      });
    }
  }, [mapLoaded, filteredVehicles]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: 500,
          bgcolor: '#e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!mapLoaded && (
          <Typography variant="body2" color="text.secondary">
            Loading map...
          </Typography>
        )}
      </Box>
      
      {/* Vehicle count badge */}
      <Box sx={{
        position: 'absolute',
        bottom: 146,
        right: 16,
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        px: 2,
        py: 1,
        borderRadius: 1,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <Typography variant="body2" fontWeight={700} color="#1a237e">
          {filteredVehicles.length} Vehicle{filteredVehicles.length !== 1 ? 's' : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {filter === 'all' ? 'All statuses' : filter.charAt(0).toUpperCase() + filter.slice(1)}
        </Typography>
      </Box>

      {/* Legend */}
      <Box sx={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        bgcolor: 'rgba(255,255,255,0.97)',
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
        border: '1px solid #e2e8f0',
      }}>
        <Typography variant="caption" fontWeight={700} color="#1a237e" sx={{ mb: 0.8, display: 'block', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Map Legend
        </Typography>
        {[
          { label: 'Active / Moving',  color: '#1565c0', bg: '#1565c0' },
          { label: 'Assigned / Parked',color: '#16a34a', bg: '#16a34a' },
          { label: 'Maintenance',      color: '#dc2626', bg: '#dc2626' },
        ].map(({ label, color, bg }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.4 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: 0.8, bgcolor: bg, border: `2px solid ${color}`, flexShrink: 0 }} />
            <Typography variant="caption" fontSize={10} color="text.secondary" fontWeight={600}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

